import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogPositioner,
  DialogTitle,
  DialogCloseTrigger,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Upload, X, FileText, ChevronDown, Loader2 } from 'lucide-react'
import { createToaster } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { useUploadDocument, useUpdateDocument } from '../hooks/useDocuments'
import { useFolders } from '../hooks/useFolders'
import { useCategorySuggestion } from '../hooks/useCategorySuggestion'
import type { DocumentCategory, DocumentType, DocumentWithRelations, DocumentFolder } from '../types'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

interface DocumentUploadModalProps {
  isOpen: boolean
  onClose: () => void
  mode?: 'create' | 'edit'
  editDocument?: DocumentWithRelations
  preselectedResidenceId?: string
  preselectedFolderId?: string
  preselectedCategory?: DocumentCategory
}

const DOCUMENT_TYPES_BY_CATEGORY: Record<DocumentCategory, DocumentType[]> = {
  constitutif: [
    'reglement_copropriete',
    'etat_descriptif_division',
    'acte_fondateur',
    'modificatif',
  ],
  ag: ['convocation_ag', 'pv_ag', 'feuille_presence', 'resolution'],
  comptabilite: [
    'budget_previsionnel',
    'compte_annuel',
    'appel_fonds',
    'situation_comptable',
    'rapprochement_bancaire',
  ],
  technique: [
    'diagnostic_dpe',
    'diagnostic_amiante',
    'diagnostic_plomb',
    'diagnostic_electricite',
    'diagnostic_gaz',
    'rapport_visite',
    'plan',
    'controle_reglementaire',
  ],
  travaux: ['devis', 'facture', 'contrat', 'photo', 'pv_reception'],
  contrats: ['contrat', 'devis', 'facture'],
  sinistres: ['declaration_sinistre', 'expertise', 'correspondance_assurance'],
  juridique: ['courrier', 'autre'],
  correspondance: ['courrier', 'email', 'autre'],
  coproprietaire: [
    'piece_identite',
    'acte_propriete',
    'rib',
    'mandat_sepa',
    'justificatif_domicile',
  ],
  autre: ['autre'],
}

export function DocumentUploadModal({
  isOpen,
  onClose,
  mode = 'create',
  editDocument,
  preselectedResidenceId,
  preselectedFolderId,
  preselectedCategory,
}: DocumentUploadModalProps) {
  const { t } = useTranslation()
  const { data: properties = [] } = useProperties()
  const uploadDocument = useUploadDocument()
  const updateDocument = useUpdateDocument()

  const isEditMode = mode === 'edit' && editDocument

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [residenceId, setResidenceId] = useState(
    isEditMode ? (editDocument.residence_id || '') : (preselectedResidenceId || '')
  )
  const [folderId, setFolderId] = useState<string>(
    isEditMode ? editDocument.folder_id || '' : preselectedFolderId || ''
  )
  const [category, setCategory] = useState<DocumentCategory | ''>(
    isEditMode ? (editDocument.category || '') : (preselectedCategory || '')
  )
  const [documentType, setDocumentType] = useState<DocumentType | ''>(
    isEditMode ? (editDocument.document_type as DocumentType) || '' : ''
  )
  const [displayName, setDisplayName] = useState(
    isEditMode ? editDocument.display_name : ''
  )
  const [documentDate, setDocumentDate] = useState(
    isEditMode && editDocument.document_date ? editDocument.document_date.split('T')[0] : ''
  )
  const [notes, setNotes] = useState(
    isEditMode ? editDocument.notes || '' : ''
  )

  // Load folders for selected residence (EXCLUDE the residence-level "Copropriétaires" folder)
  const { data: residenceFolders = [] } = useFolders(residenceId || undefined)

  // Get agency_id from the selected residence
  const { data: selectedResidence } = useQuery({
    queryKey: ['residence', residenceId],
    queryFn: async () => {
      if (!residenceId) return null
      const { data, error } = await supabase
        .from('residences')
        .select('agency_id')
        .eq('id', residenceId)
        .single()
      if (error) throw error
      return data
    },
    enabled: !!residenceId,
  })

  // Load agency-level folders (co-owner folders with residence_id = NULL)
  const { data: agencyFolders = [] } = useQuery({
    queryKey: ['agency-folders', selectedResidence?.agency_id],
    queryFn: async () => {
      if (!selectedResidence?.agency_id) return []

      const { data, error } = await supabase
        .from('document_folders')
        .select('*')
        .eq('agency_id', selectedResidence.agency_id)
        .is('residence_id', null)
        .is('deleted_at', null)
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true })

      if (error) {
        console.error('❌ AGENCY FOLDERS ERROR:', error)
        throw error
      }
      return data as DocumentFolder[]
    },
    enabled: !!selectedResidence?.agency_id,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes to avoid refetching
  })

  // Combine residence folders and co-owner folders with virtual hierarchy
  const allFolders = useMemo(() => {
    // Find the residence-level "Copropriétaires" folder
    const residenceCopropsFolder = residenceFolders.find(
      (f) => f.slug === 'coproprietaires' || f.suggested_category === 'coproprietaire'
    )

    // Extract co-owner physical folders (with owner_id)
    const coOwnerFolders = agencyFolders.filter((f) => f.owner_id)

    // If we have a residence "Copropriétaires" folder, remap co-owner folders to be its children
    if (residenceCopropsFolder && coOwnerFolders.length > 0) {
      // Remap co-owner folders to have the residence "Copropriétaires" as parent
      const remappedCoOwnerFolders = coOwnerFolders.map((f) => ({
        ...f,
        parent_folder_id: residenceCopropsFolder.id,
      }))

      return [...residenceFolders, ...remappedCoOwnerFolders]
    }

    // Fallback: just combine everything
    return [...residenceFolders, ...coOwnerFolders]
  }, [residenceFolders, agencyFolders])

  // Get category suggestion based on folder and filename
  const categorySuggestion = useCategorySuggestion({
    folderId: folderId || undefined,
    filename: selectedFile?.name,
    folders: allFolders,
  })

  // Sync preselected values when modal opens
  useEffect(() => {
    if (isOpen) {
      setResidenceId(preselectedResidenceId || '')
      setFolderId(preselectedFolderId || '')
      setCategory(preselectedCategory || '')
    }
  }, [isOpen, preselectedResidenceId, preselectedFolderId, preselectedCategory])

  // Auto-suggest category based on folder or filename
  useEffect(() => {
    // Only auto-suggest if user hasn't manually selected a category
    // And if we have a suggestion
    if (categorySuggestion.category && !category) {
      setCategory(categorySuggestion.category)
    }
  }, [categorySuggestion.category, category])

  // Reset form when modal closes
  const handleClose = () => {
    setSelectedFile(null)
    setResidenceId(preselectedResidenceId || '')
    setFolderId(preselectedFolderId || '')
    setCategory(preselectedCategory || '')
    setDocumentType('')
    setDisplayName('')
    setDocumentDate('')
    setNotes('')
    onClose()
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]
      setSelectedFile(file)
      if (!displayName) {
        setDisplayName(file.name)
      }
    }
  }, [displayName])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setSelectedFile(file)
      if (!displayName) {
        setDisplayName(file.name)
      }
    }
  }

  const handleUpload = async () => {
    // Validation for create mode
    if (!isEditMode && (!selectedFile || !residenceId || !displayName)) {
      toaster.error({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs obligatoires (fichier, résidence, nom)',
      })
      return
    }

    // Validation for edit mode
    if (isEditMode && !displayName) {
      toaster.error({
        title: 'Erreur',
        description: 'Le nom du document est obligatoire',
      })
      return
    }

    try {
      if (isEditMode) {
        // Update existing document
        const updates = {
          display_name: displayName,
          category: category || undefined,
          folder_id: folderId || undefined,
          document_type: documentType || undefined,
          document_date: documentDate || undefined,
          notes: notes || undefined,
        }

        await updateDocument.mutateAsync({
          documentId: editDocument.id,
          updates,
        })

        toaster.success({
          title: 'Document modifié',
          description: `Le document "${displayName}" a été modifié avec succès`,
        })
      } else {
        // Upload new document
        const metadata = {
          residence_id: residenceId,
          category: category || undefined,
          folder_id: folderId || undefined,
          document_type: documentType || undefined,
          display_name: displayName,
          document_date: documentDate || undefined,
          notes: notes || undefined,
        }

        console.log('📤 Upload metadata:', metadata)
        console.log('  - folderId:', folderId)
        console.log('  - Category (selected/suggested):', category)
        console.log('  - Suggestion source:', categorySuggestion.source)
        console.log('  - folders available:', allFolders.length)

        await uploadDocument.mutateAsync({
          file: selectedFile!,
          metadata,
        })

        toaster.success({
          title: 'Document uploadé',
          description: `Le document "${displayName}" a été uploadé avec succès`,
        })
      }

      handleClose()
    } catch (error) {
      console.error('Upload/Update error:', error)
      toaster.error({
        title: isEditMode ? 'Erreur de modification' : 'Erreur d\'upload',
        description:
          error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'upload',
      })
    }
  }

  const availableTypes = category ? DOCUMENT_TYPES_BY_CATEGORY[category] : []

  // Build folder hierarchy for display (with indentation)
  const buildFolderHierarchy = (
    folders: DocumentFolder[],
    parentId: string | null = null,
    level: number = 0
  ): Array<{ id: string; name: string; level: number }> => {
    const result: Array<{ id: string; name: string; level: number }> = []
    const children = folders.filter((f) => f.parent_folder_id === parentId)

    children.forEach((folder) => {
      result.push({ id: folder.id, name: folder.name, level })
      result.push(...buildFolderHierarchy(folders, folder.id, level + 1))
    })

    return result
  }

  const folderHierarchy = buildFolderHierarchy(allFolders)

  // Build display label for folders with hierarchy indentation
  const getFolderDisplayLabel = (folder: { id: string; name: string; level: number }, level: number): string => {
    const indent = '\u00A0'.repeat(level * 4)
    const prefix = level > 0 ? '└─ ' : ''
    return `${indent}${prefix}${folder.name}`
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="600px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Modifier le document' : 'Uploader un document'}
            </DialogTitle>
          </DialogHeader>

        <DialogBody>
          <VStack gap={4} align="stretch">
            {/* File Upload Zone - Only in create mode */}
            {!isEditMode && (
              <Box
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={dragActive ? 'brand.500' : 'gray.300'}
                borderRadius="lg"
                p={8}
                textAlign="center"
                bg={dragActive ? 'brand.50' : 'gray.50'}
                cursor="pointer"
                transition="all 0.2s"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('file-upload')?.click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                />

                {selectedFile ? (
                  <VStack gap={2}>
                    <FileText size={48} color="#3182CE" />
                    <Text fontWeight="medium">{selectedFile.name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Text>
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFile(null)
                      }}
                    >
                      <X size={16} />
                      <Text ml={2}>Supprimer</Text>
                    </Button>
                  </VStack>
                ) : (
                  <VStack gap={2}>
                    <Upload size={48} color="#A0AEC0" />
                    <Text fontWeight="medium">Glissez-déposez un fichier ici</Text>
                    <Text fontSize="sm" color="gray.600">
                      ou cliquez pour parcourir
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      PDF, Word, Excel, Images (max 100 MB)
                    </Text>
                  </VStack>
                )}
              </Box>
            )}

            {/* Display filename in edit mode */}
            {isEditMode && (
              <Box p={4} bg="gray.50" borderRadius="md">
                <HStack gap={2}>
                  <FileText size={20} color="#3182CE" />
                  <Text fontWeight="medium" fontSize="sm">
                    {editDocument.original_filename}
                  </Text>
                </HStack>
              </Box>
            )}

            {/* Residence - Required (readonly in edit mode) */}
            <Field.Root required>
              <Field.Label>Résidence</Field.Label>
              <NativeSelectRoot
                position="relative"
                pointerEvents={isEditMode ? 'none' : 'auto'}
                opacity={isEditMode ? 0.6 : 1}
              >
                <NativeSelectField
                  value={residenceId}
                  onChange={(e) => setResidenceId(e.target.value)}
                  paddingRight="10"
                >
                  <option value="">Sélectionner une résidence</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.residence_key
                        ? `${property.residence_key} - ${property.name}`
                        : property.name}
                    </option>
                  ))}
                </NativeSelectField>
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  color="gray.500"
                >
                  <ChevronDown size={16} />
                </Box>
              </NativeSelectRoot>
              {isEditMode && (
                <Field.HelperText>
                  La résidence ne peut pas être modifiée
                </Field.HelperText>
              )}
            </Field.Root>

            {/* Category - Optional with smart suggestion */}
            <Field.Root>
              <Field.Label>Catégorie (optionnel)</Field.Label>
              <NativeSelectRoot position="relative">
                <NativeSelectField
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as DocumentCategory)
                    setDocumentType('') // Reset document type when category changes
                  }}
                  paddingRight="10"
                >
                  <option value="">Aucune catégorie</option>
                  <option value="constitutif">{t('documents.categories.constitutif')}</option>
                  <option value="ag">{t('documents.categories.ag')}</option>
                  <option value="comptabilite">{t('documents.categories.comptabilite')}</option>
                  <option value="travaux">{t('documents.categories.travaux')}</option>
                  <option value="technique">{t('documents.categories.technique')}</option>
                  <option value="contrats">{t('documents.categories.contrats')}</option>
                  <option value="sinistres">{t('documents.categories.sinistres')}</option>
                  <option value="juridique">{t('documents.categories.juridique')}</option>
                  <option value="correspondance">
                    {t('documents.categories.correspondance')}
                  </option>
                  <option value="coproprietaire">{t('documents.categories.coproprietaire')}</option>
                  <option value="autre">{t('documents.categories.autre')}</option>
                </NativeSelectField>
                <Box
                  position="absolute"
                  right="3"
                  top="50%"
                  transform="translateY(-50%)"
                  pointerEvents="none"
                  color="gray.500"
                >
                  <ChevronDown size={16} />
                </Box>
              </NativeSelectRoot>
              {categorySuggestion.category && category === categorySuggestion.category && (
                <Field.HelperText color="blue.600">
                  ✨ {categorySuggestion.message || 'Catégorie suggérée automatiquement'}
                </Field.HelperText>
              )}
              {!category && (
                <Field.HelperText>
                  Laissez vide pour une classification libre, ou sélectionnez pour faciliter la recherche
                </Field.HelperText>
              )}
            </Field.Root>

            {/* Folder - Optional */}
            {allFolders.length > 0 && (
              <Field.Root>
                <Field.Label>Dossier (optionnel)</Field.Label>
                <NativeSelectRoot position="relative">
                  <NativeSelectField
                    value={folderId}
                    onChange={(e) => setFolderId(e.target.value)}
                    paddingRight="10"
                  >
                    <option value="">Racine de la résidence</option>
                    {folderHierarchy.map((folder) => {
                      return (
                        <option key={folder.id} value={folder.id}>
                          {getFolderDisplayLabel(folder, folder.level)}
                        </option>
                      )
                    })}
                  </NativeSelectField>
                  <Box
                    position="absolute"
                    right="3"
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                  >
                    <ChevronDown size={16} />
                  </Box>
                </NativeSelectRoot>
                <Field.HelperText>
                  {folderId
                    ? 'Le document sera rangé dans ce dossier'
                    : 'Choisissez un dossier pour organiser votre document'}
                </Field.HelperText>
              </Field.Root>
            )}

            {/* Document Type - Optional */}
            {category && availableTypes.length > 0 && (
              <Field.Root>
                <Field.Label>Type de document (optionnel)</Field.Label>
                <NativeSelectRoot position="relative">
                  <NativeSelectField
                    value={documentType}
                    defaultValue=""
                    onChange={(e) => setDocumentType(e.target.value as DocumentType)}
                    paddingRight="10"
                  >
                    <option value="">Sélectionner un type</option>
                    {availableTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </NativeSelectField>
                  <Box
                    position="absolute"
                    right="3"
                    top="50%"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                    color="gray.500"
                  >
                    <ChevronDown size={16} />
                  </Box>
                </NativeSelectRoot>
              </Field.Root>
            )}

            {/* Display Name - Required */}
            <Field.Root required>
              <Field.Label>Nom du document</Field.Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex: Règlement de copropriété 2024"
              />
            </Field.Root>

            {/* Document Date - Optional */}
            <Field.Root>
              <Field.Label>Date du document (optionnel)</Field.Label>
              <Input
                type="date"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
              />
            </Field.Root>

            {/* Notes - Optional */}
            <Field.Root>
              <Field.Label>Notes (optionnel)</Field.Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Informations complémentaires..."
              />
            </Field.Root>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <HStack gap={2}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={uploadDocument.isPending || updateDocument.isPending}
            >
              Annuler
            </Button>
            <Button
              colorPalette="brand"
              onClick={handleUpload}
              disabled={
                isEditMode
                  ? !displayName || updateDocument.isPending
                  : !selectedFile || !residenceId || !displayName || uploadDocument.isPending
              }
              loading={uploadDocument.isPending || updateDocument.isPending}
            >
              {uploadDocument.isPending || updateDocument.isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <Text ml={2}>{isEditMode ? 'Modification en cours...' : 'Upload en cours...'}</Text>
                </>
              ) : (
                isEditMode ? 'Modifier' : 'Uploader'
              )}
            </Button>
          </HStack>
        </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
